/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useState } from "react";
import { ICustomerRequester } from "../../../../interfaces/customerRequesters";
import { useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useEntityNavigation } from "@/hooks/useEntityNavigation";
import useMounted from "../../../../hooks/mounted";
import { debounce } from "lodash";
import { createCustomerRequester, deleteCustomerRequester, listCustomerRequesters, showCustomerRequester, updateCustomerRequester } from "@/services";
import { updateValue } from "@/utils/updateValue";
import { useEntityQueryParams } from "@/hooks/useEntityQueryParams";
import { useEntitySearch } from "@/hooks/useEntitySearch";
import {
  notifyDeleteResults,
  runDeleteBatch,
} from "@/utils/deleteResults";

const defaultCustomerRequester: ICustomerRequester = {
  name: "",
  email: "",
  phone: "",
  isActive: true,
  idCustomer: 0,
  customer: undefined,
  createdIn: undefined,
  updatedIn: undefined,
};

export const useCustomerRequesters = ({
  disabledShowId,
  disabledGetAll,
  disabledNavigation,
  filter: filterInitial = {},
  includes = [],
}: {
  disabledGetAll?: boolean;
  disabledShowId?: boolean;
  /** Em uso dentro de modal: create/update não navegam ao concluir. */
  disabledNavigation?: boolean;
  filter?: Record<string, any>;
  includes?: string[];
}) => {
  const { idCustomerRequester: id, idCustomer } = useParams();
  const location = useLocation();
  const [customerRequesters, setCustomerRequesters] = useState<ICustomerRequester[]>([]);
  const [customerRequester, setCustomerRequester] = useState<ICustomerRequester>(defaultCustomerRequester);
  const { filter, handleFilter, page, setPage, limit, setLimit } =
    useEntityQueryParams({ filter: filterInitial });
  const [totalItems, setTotalItems] = useState<number>(0);
  // Dentro de um cliente o solicitante vive sob ele, e o voltar devolve para o cliente
  const { goAdd, goEdit, goBack } = useEntityNavigation({
    basePath: idCustomer
      ? `/towermanagement/customers/${idCustomer}/requesters`
      : "/towermanagement/customer_requesters",
    pathBack: idCustomer
      ? `/towermanagement/customers/${idCustomer}`
      : "/towermanagement/customer_requesters",
  });

  const [loading, setLoading] = useState<boolean>(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleCustomerRequester = (key: string, value: any, custom?: (v: any) => any) => {
    setCustomerRequester((curr) => {
      let newData = updateValue(curr, key, value);
      if (custom) newData = custom(newData);
      return { ...curr, ...newData };
    });
  };



  const getAll = useCallback(
    debounce(
      ({
        page = 1,
        limit,
        filter: filterG = {},
      }: {
        page?: number;
        limit?: number;
        filter?: Record<string, any>;
      }) => {
        setLoading(true);
        console.log(filter);
        listCustomerRequesters({
          includes: ["customer", ...includes],
          page,
          filter: { ...filter, ...filterG },
          limit,
        })
          .then(({ customerRequesters: listCustomerRequesters, limit, currentPage, totalItems }) => {
            setCustomerRequesters(listCustomerRequesters);
            
            setLimit(Number(limit));
            setTotalItems(Number(totalItems));
          })
          .finally(() => setLoading(false));
      },
      500
    ),
    []
  );

  const search = useEntitySearch({
    list: listCustomerRequesters,
    key: "customerRequesters",
    includes: ["customer", ...includes],
    filter,
  });


  const get = ({ id }: { id: string | number }) => {
    setLoading(true);
    showCustomerRequester({ id, includes })
      .then(({ customerRequester: customerRequesterShow }) => {
        setCustomerRequester(customerRequesterShow);
      })
      .finally(() => setLoading(false));
  };

  const create = () => {
    setLoading(true);
    return createCustomerRequester({ customerRequester: { ...customerRequester, idCustomer: Number(idCustomer) } })
      .then(({ customerRequester: customerRequesterCreate }) => {
        toast.success(`Solicitante (${customerRequesterCreate.name}) criado com sucesso!`);
        if (disabledNavigation) return;
        goEdit(customerRequesterCreate.id!);
      })
      .finally(() => setLoading(false));
  };

  const update = () => {
    setLoading(true);
    return updateCustomerRequester({ id: customerRequester.id!, customerRequester })
      .then(({ customerRequester: customerRequesterUpdate }) => {
        toast.success(`Solicitante (${customerRequesterUpdate.name}) atualizado com sucesso!`);
        if (disabledNavigation) return;
        goBack();
      })
      .finally(() => setLoading(false));
  };


  useMounted(() => {
    getAll({ page, limit, filter });
  }, [page, limit, filter]);

  useEffect(() => {
    if (id && !disabledShowId) {
      if (id !== "new") get({ id: id });
      return;
    }
    if (!disabledGetAll) getAll({ limit });
  }, []);

  const deleteMany = async (ids: (string | number)[]) => {
    if (!ids?.length) return;
    setLoading(true);

    try {
      const results = await runDeleteBatch(ids, (entityId) =>
        deleteCustomerRequester({ id: entityId }),
      );

      notifyDeleteResults(results, {
        singular: "Solicitante",
        plural: "solicitantes",
        gender: "m",
      });
    } catch {
      // erro sem resposta do servidor (rede/timeout): o interceptor já avisou
    } finally {
      setLoading(false);
      getAll({ page, limit, filter });
    }
  };

  return {
    deleteMany,
    customerRequesters,
    customerRequester,
    setCustomerRequester,
    get,
    loading,
    setPage,
    setLimit,
    page,
    limit,
    goEdit,
    goBack,
    goAdd,
    handleCustomerRequester,
    create,
    totalItems,
    id,
    update,
    getAll,
    search,
    handleFilter,
  };
};
